// src/pages/receptionist/BookAppointment.js
import { useEffect, useState } from "react";
import axios from "axios";
import * as Yup from "yup";
import {
  Container,
  Row,
  Col,
  Button,
  Table,
  Form 
} from "react-bootstrap";
import Sidebar from "../../components/Sidebar";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import ReAppointmentModal from "../../components/ReAppointmentModal";

const BASE_URL = process.env.REACT_APP_BASE_URL;

// ✅ Reusable validation rule
const requiredTrimmedString = (msg) =>
  Yup.string()
    .required(msg)
    .test("no-spaces-only", `${msg} cannot be empty or just spaces`, (value) =>
      value ? value.trim().length > 0 : false
    );

// ✅ Validation schema
const validationSchema = Yup.object({
  doctorId: Yup.string().required("Select a doctor"),
  appointmentDate: Yup.string().required("Select a date"),
  appointmentTime: Yup.string().required("Select a time"),
  reason: requiredTrimmedString("Reason"),
  additionalPhone: Yup.string()
    .optional()
    .matches(/^\d{10}$/, "Additional phone number must be exactly 10 digits"),
});

// ✅ Input sanitizers
const preventSpaces = (e) => {
  if (e.key === " " && (!e.target.value || /^\s*$/.test(e.target.value + e.key))) {
    e.preventDefault();
  }
};
const preventPasteSpaces = (e) => {
  if (/^\s*$/.test(e.clipboardData.getData("text"))) {
    e.preventDefault();
  }
};

const BookAppointment = () => {
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState([]);
  const [filters, setFilters] = useState({
    receiptNo: "",
    patientName: "",
    patientPhone: "",
    doctorName: "",
    appointmentDate: "",
    status: "",
  });

  const [showModal, setShowModal] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [hospitalDetails, setHospitalDetails] = useState({
    hospital_id: "",
    subhospital_id: "",
  });
  const [doctors, setDoctors] = useState([]);
  const [availableTimes, setAvailableTimes] = useState([]);

  // ✅ Fetch doctors & appointments
  useEffect(() => {
      
  const token = localStorage.getItem("token");
  const receptionist_id =localStorage.getItem("receptionist_id");

  const user =
    JSON.parse(localStorage.getItem("authUser")) ||
    JSON.parse(localStorage.getItem("user"));

  if (!token || !user) {
    toast.error("Session expired. Please log in again.");
    return;
  }

  const { hospital_id, sub_hospital_id } = user;
  setHospitalDetails({ hospital_id, subhospital_id: sub_hospital_id });

  axios.get(`${BASE_URL}/api/patientAppointment/doctors`, {
  headers: { Authorization: `Bearer ${token}` },
  params: { 
    hospital_id: hospital_id,
    subhospital_id: sub_hospital_id,
    receptionist_id: receptionist_id
  },
})
    .then((res) => {
  const formattedDoctors = res.data.map((doc) => (
   {
    id: doc.id,
    name: doc.doctor_name,
    specialties: doc.specialties

  }));
  setDoctors(formattedDoctors);
})
    .catch(() => toast.error("Failed to load doctors."));

  fetchAppointments(token, hospital_id, sub_hospital_id);
}, []);

  const fetchAppointments = (token, hospital_id, subhospital_id) => {
    axios
      .get(`${BASE_URL}/api/patientAppointment/appointments`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { hospital_id, subhospital_id },
      })
      .then((res) => {
        const sorted = res.data.sort((a, b) => b.appointmentId - a.appointmentId);
        setAppointments(sorted);
      })
      .catch(() => toast.error("Failed to fetch appointments."));
  };

  // ✅ Re-appointment Modal Logic
  const handleReAppointmentClick = async (appointment) => {
    const token = localStorage.getItem("token");
    if (!token) return toast.error("Session expired. Please log in again.");

    try {
      const res = await axios.get(`${BASE_URL}/api/patientAppointment/get-patient-id`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { patientName: appointment.patientName },
      });

      if (!res.data.patient_id) return toast.error("Patient ID not found.");

      setSelectedPatient({
        id: res.data.patient_id,
        patientName: appointment.patientName,
        patientPhone: appointment.patientPhone,
        hospital_id: appointment.hospital_id,
        subhospital_id: appointment.subhospital_id,
      });
      setShowModal(true);
    } catch {
      toast.error("Failed to fetch patient ID.");
    }
  };

  const handleReAppointmentSubmit = async (values, { resetForm }) => {
    const token = localStorage.getItem("token");
    if (!token || !selectedPatient?.id) return toast.error("Missing patient details.");

    const [start_time, end_time] = values.appointmentTime.split("-").map((t) => t.trim());
    const payload = {
      patient_id: selectedPatient.id,
      doctor_id: values.doctorId,
      appointment_date: values.appointmentDate,
      start_time: `${values.appointmentDate} ${start_time}:00`,
      end_time: `${values.appointmentDate} ${end_time}:00`,
      reason: values.reason,
      hospital_id: selectedPatient.hospital_id,
      subhospital_id: selectedPatient.subhospital_id,
    };

    try {
      await axios.post(`${BASE_URL}/api/patientAppointment/create-appointment`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Re-Appointment booked!");
      setShowModal(false);
      fetchAppointments(token, selectedPatient.hospital_id, selectedPatient.subhospital_id);
      resetForm();
    } catch {
      toast.error("Error booking re-appointment.");
    }
  };

  const handleFilterChange = (e) =>
    setFilters((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const filteredAppointments = appointments.filter((a) =>
    Object.entries(filters).every(([key, value]) =>
      (a[key]?.toString().toLowerCase() || "").includes(value.toLowerCase())
    )
  );

  return (
    <div className="d-flex">
      <Sidebar />
      <Container fluid>
        {/* Header */}
        <Row className="align-items-center my-3">
          <Col>
            <h3 className="fw-bold primary-class">Appointments</h3>
          </Col>
          <Col className="text-md-end mt-2 mt-md-0 ">
            <Button className="global-btn" onClick={() => navigate("/receptionist/book-appointment")}>
              New Appointment
            </Button>
          </Col>
        </Row>

        {/* Table */}
        <Row>
          <Col>
            <Table responsive bordered hover className="align-middle">
              <thead className="table-light">
                <tr>
                  <th>Sr.no.</th>
                  {["receiptNo", "patientName", "patientPhone", "doctorName", "appointmentDate", "status"].map(
                    (field) => (
                      <th key={field}>
                        <div className="d-flex flex-column">
                          <span className="fw-semibold text-capitalize">{field}</span>
                          <Form.Control
                            size="sm"
                            type={field === "appointmentDate" ? "date" : "text"}
                            name={field}
                            value={filters[field]}
                            onChange={handleFilterChange}
                          />
                        </div>
                      </th>
                    )
                  )}
                  <th>Re-Appointment</th>
                </tr>
              </thead>
              <tbody>
                {filteredAppointments.length ? (
                  filteredAppointments.map((a, i) => (
                    <tr key={a.id || a.appointmentId}>
                      <td>{i + 1}</td>
                      <td>{a.receiptNo || "N/A"}</td>
                      <td>{a.patientName}</td>
                      <td>{a.patientPhone}</td>
                      <td>{a.doctorName}</td>
                      <td>{a.appointmentDate}</td>
                      <td>{a.status}</td>
                      <td>
                        <Button className="btn-custom" size="sm" onClick={() => handleReAppointmentClick(a)}>
                          Re-Book
                        </Button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" className="text-center text-danger">
                      No appointments found.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Col>
        </Row>

        {/* Modal */}
        <ReAppointmentModal
          show={showModal}
          onHide={() => setShowModal(false)}
          selectedPatient={selectedPatient}
          doctors={doctors}
          availableTimes={availableTimes}
          validationSchema={validationSchema}
          onSubmit={handleReAppointmentSubmit}
          handleDoctorChange={(id, setFieldValue) => {
            setFieldValue("doctorId", id);
            setFieldValue("appointmentDate", "");
            setFieldValue("appointmentTime", "");
            setAvailableTimes([]);
          }}
          handleDateChange={async (date, doctorId, setFieldValue) => {
            setFieldValue("appointmentDate", date);
            setFieldValue("appointmentTime", "");
            if (doctorId && date) {
              const token = localStorage.getItem("token");
              try {
                const res = await axios.get(
                  `${BASE_URL}/api/patientAppointment/doctor-availability`,
                  {
                    headers: { Authorization: `Bearer ${token}` },
                    params: {
                      doctor_id: doctorId,
                      date,
                      hospital_id: hospitalDetails.hospital_id,
                      subhospital_id: hospitalDetails.subhospital_id,
                    },
                  }
                );
                setAvailableTimes(res.data);
              } catch {
                toast.error("Error fetching available times.");
              }
            }
          }}
          preventSpaces={preventSpaces}
          preventPasteSpaces={preventPasteSpaces}
        />
      </Container>
    </div>
  );
};

export default BookAppointment;
